import { BatteryChargingFull, Lens, Lock } from '@mui/icons-material';
import {
  Box,
  SvgIconOwnProps,
  SvgIconProps,
  SxProps,
  Theme,
  Tooltip,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import {
  BidBasicDetails,
  BidDTO,
  Company,
  CurrentUser,
  OfferDTO,
  UserFull,
} from '@/store/api';

import {
  ComplianceDocumentStatus,
  ComplianceStatus,
  ConstructionStage,
  DiligenceStatus,
  LongLeadEquipmentStatus,
  NoticeToProceedStatus,
} from '@/types/compliance';

import { SolarTechnologyIcon, WindTechnologyIcon } from './customIcons';
import {
  BID_ACTIVE_STATUSES,
  BID_STATUS,
  COMPANY_CREDIT_PROFILE,
  CONTRACT_TYPE,
  CONTRACT_TYPE_BUILD_STAGE,
  CompanyRole,
  GROUPED_CONTRACT_TYPE,
  NEGOTIATION_STATUS_LABELS,
  OFFER_ACTIVE_STATUSES,
  OFFER_STATUS,
  POSITION_TAGS,
  POSITION_TAGS_OPTIONS,
  PRODUCT_TYPE,
  TECHNOLOGY_TYPE,
  UserRole,
  VolumeUnit,
} from './enums';

import {
  customIconWrapperSx,
  exactMatchIconSx,
  exactMatchTooltipSx,
  technologyIconWrapperSx,
} from './helpers.styles';

export const COMPLIANCE_RED = '#f9644d';
export const COMPLIANCE_YELLOW = '#ffb660';

export const getCompanyRoleColor = (role: string) => {
  switch (role) {
    case CompanyRole.SELLER_ONLY:
      return 'primary';
    case CompanyRole.BUYER_ONLY:
      return 'info';
    case CompanyRole.BI_DIRECTIONAL:
      return 'warning';
    case CompanyRole.ADVISOR:
      return 'default';
    default:
      return 'default';
  }
};

/**
 * Returns the background color of a diligence status chip.
 *
 * Statuses that have not started (or are blocked) are red, statuses in progress are
 * yellow, and completed statuses are green. Not applicable and unselected statuses
 * use the neutral elevated background. The battery statuses (construction stage,
 * notice to proceed, long-lead equipment) share no enum value with the compliance
 * statuses, so each case matches exactly one meaning.
 *
 * @param status the status value shown on the chip
 * @returns a theme palette path or color hex value for the chip background
 */
export const getComplianceStatusColor = (status: DiligenceStatus) => {
  switch (status) {
    case ComplianceDocumentStatus.NOT_APPLICABLE:
    case ComplianceStatus.NOT_APPLICABLE:
      return 'background.elevated';
    case ComplianceStatus.NOT_SUBMITTED:
    case ComplianceDocumentStatus.WORK_IN_PROGRESS:
    case LongLeadEquipmentStatus.NOT_ORDERED:
    case NoticeToProceedStatus.NOT_ISSUED:
    case ConstructionStage.PRE_NTP:
      return COMPLIANCE_RED;
    case ComplianceStatus.SUBMITTED:
    case ComplianceDocumentStatus.DRAFT_AVAILABLE:
    case LongLeadEquipmentStatus.ORDERED:
    case LongLeadEquipmentStatus.DELIVERY_SCHEDULED:
    case NoticeToProceedStatus.LIMITED_NTP:
    case ConstructionStage.NTP_ISSUED:
    case ConstructionStage.MOBILIZED:
    case ConstructionStage.EQUIPMENT_ON_SITE:
      return COMPLIANCE_YELLOW;
    case ComplianceStatus.APPROVED:
    case ComplianceDocumentStatus.EXECUTED:
    case LongLeadEquipmentStatus.DELIVERED:
    case NoticeToProceedStatus.FULL_NTP:
    case ConstructionStage.COMMISSIONING:
      return 'success.main';
    default:
      return 'background.elevated';
  }
};

/**
 * Returns the text color of a diligence status chip, paired with
 * {@link getComplianceStatusColor}. Every status on a red, yellow or green background
 * uses the contrast text color; not applicable and unselected statuses use the
 * primary text color.
 *
 * @param status the status value shown on the chip
 * @returns a theme palette path for the chip text
 */
export const getComplianceStatusTextColor = (status: DiligenceStatus) => {
  switch (status) {
    case ComplianceStatus.NOT_SUBMITTED:
    case ComplianceDocumentStatus.WORK_IN_PROGRESS:
    case ComplianceStatus.APPROVED:
    case ComplianceDocumentStatus.DRAFT_AVAILABLE:
    case ComplianceStatus.SUBMITTED:
    case ComplianceDocumentStatus.EXECUTED:
    case LongLeadEquipmentStatus.NOT_ORDERED:
    case LongLeadEquipmentStatus.ORDERED:
    case LongLeadEquipmentStatus.DELIVERY_SCHEDULED:
    case LongLeadEquipmentStatus.DELIVERED:
    case NoticeToProceedStatus.NOT_ISSUED:
    case NoticeToProceedStatus.LIMITED_NTP:
    case NoticeToProceedStatus.FULL_NTP:
    case ConstructionStage.PRE_NTP:
    case ConstructionStage.NTP_ISSUED:
    case ConstructionStage.MOBILIZED:
    case ConstructionStage.EQUIPMENT_ON_SITE:
    case ConstructionStage.COMMISSIONING:
      return 'primary.contrastText';
    default:
      return 'text.primary';
  }
};

export const formatLastLogin = (dateString: string | null) => {
  // Return empty string if dateString is null (user has never logged in)
  if (dateString === null) return '';
  return dayjs(dateString).format('MM/DD/YYYY h:mm A');
};

export const formatDateString = (dateString: string) => {
  return dayjs(dateString).format('MM/DD/YYYY');
};

export const formatDateTimeString = (dateString: string) => {
  // Times are displayed in the user's local timezone (dayjs default)
  return dayjs(dateString).format('MM/DD/YYYY h:mm A');
};

export const formatPhoneNumber = (phoneNumber: string) => {
  // Remove all non-digit characters
  const digits = phoneNumber.replaceAll(/\D/g, '');

  // If it's exactly 10 digits, format as (123) 456-7890
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Otherwise, return the original number
  return phoneNumber;
};

/**
 * Maps a company role to the list of valid user roles based on the company type permission data.
 * @param companyRole - The company role to map
 * @returns Array of valid UserRole values for the given company role
 */
export const getValidUserRolesForCompanyRole = (
  companyRole: CompanyRole
): UserRole[] => {
  // Mapping based on the t_company_type_permission.csv data
  const roleMapping: Record<CompanyRole, UserRole[]> = {
    [CompanyRole.ADVISOR]: [
      UserRole.VIEW_ONLY_BUYER_ADVISOR,
      UserRole.VIEW_ONLY_SELLER_ADVISOR,
      UserRole.VIEW_ONLY_BI_DIRECTIONAL_ADVISOR,
      UserRole.BUYER_TRADER_ADVISOR,
      UserRole.SELLER_TRADER_ADVISOR,
      UserRole.BI_DIRECTIONAL_TRADER_ADVISOR,
      UserRole.ADVISOR_COMPANY_ADMIN,
    ],
    [CompanyRole.BI_DIRECTIONAL]: [
      UserRole.VIEW_ONLY_BI_DIRECTIONAL,
      UserRole.BI_DIRECTIONAL_LEGAL,
      UserRole.BI_DIRECTIONAL_RFQ_TRADER,
      UserRole.BI_DIRECTIONAL_AUTO_EX_TRADER,
      UserRole.BI_DIRECTIONAL_COMPANY_ADMIN,
      UserRole.AUTHORIZED_SIGNATORY,
      UserRole.VIEW_ONLY_BI_DIRECTIONAL_ADVISOR,
      UserRole.BI_DIRECTIONAL_TRADER_ADVISOR,
    ],
    [CompanyRole.BUYER_ONLY]: [
      UserRole.VIEW_ONLY_BUYER,
      UserRole.BUYER_LEGAL,
      UserRole.BUYER_RFQ_TRADER,
      UserRole.BUYER_AUTO_EX_TRADER,
      UserRole.BUYER_COMPANY_ADMIN,
      UserRole.AUTHORIZED_SIGNATORY,
      UserRole.VIEW_ONLY_BUYER_ADVISOR,
      UserRole.BUYER_TRADER_ADVISOR,
    ],
    [CompanyRole.SELLER_ONLY]: [
      UserRole.VIEW_ONLY_SELLER,
      UserRole.SELLER_LEGAL,
      UserRole.SELLER_RFQ_TRADER,
      UserRole.SELLER_AUTO_EX_TRADER,
      UserRole.SELLER_COMPANY_ADMIN,
      UserRole.AUTHORIZED_SIGNATORY,
      UserRole.VIEW_ONLY_SELLER_ADVISOR,
      UserRole.SELLER_TRADER_ADVISOR,
    ],
  };

  return roleMapping[companyRole] || [];
};

/**
 * Checks if a user is a superuser (Platform Admin or Technical Support)
 */
export const isSuperUser = (user: CurrentUser | UserFull): boolean => {
  return user.isPlatformAdmin || user.isTechnicalSupport;
};

export const getInitials = (user: CurrentUser | UserFull) => {
  const firstName = user.firstName;
  const lastName = user.lastName;
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const COMPANY_ADMIN_ROLE_IDS = [
  UserRole.BUYER_COMPANY_ADMIN,
  UserRole.SELLER_COMPANY_ADMIN,
  UserRole.BI_DIRECTIONAL_COMPANY_ADMIN,
  UserRole.ADVISOR_COMPANY_ADMIN,
];

export const formatMethod = (method: string) => {
  return method
    .replaceAll('_', ' ')
    .toLowerCase()
    .replaceAll(/\b\w/g, l => l.toUpperCase());
};

export const getCompanyInitials = (companyName: string) => {
  const words = companyName.split(' ');
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }
  return companyName.substring(0, 2).toUpperCase();
};

// Helper function to parse date strings safely
export const parseDate = (dateString: string): Dayjs | null => {
  const parsed = dayjs(dateString?.trim());
  return parsed.isValid() ? parsed : null;
};

// Format timestamp with relative dates (Today/Yesterday) and time
export const formatTimestamp = (dateString: string): string => {
  const date = dayjs(dateString);

  if (!date.isValid()) return '';

  const today = dayjs();
  const isToday = date.isSame(today, 'day');
  const isYesterday = date.isSame(today.subtract(1, 'day'), 'day');

  const timeStr = date.format('HH:mm');

  if (isToday) return `Today, ${timeStr}`;
  if (isYesterday) return `Yesterday, ${timeStr}`;

  return date.format('MM/DD, HH:mm');
};

const _priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// For when we want to show prices with the currency symbol.
export const priceFormatter = {
  format: (value: number | null, removeTrailingZeros = true) => {
    if (value === null) return '-';

    const returnValue = _priceFormatter.format(value);

    if (removeTrailingZeros) return returnValue.replace('.00', '');

    return returnValue;
  },
};

/**
 * A unit label as an input's end adornment: the label without its leading `$`, which the start
 * adornment already shows (`$/kW-month` gives `/kW-month`). `undefined` when there is no label, so
 * the field shows no unit rather than a guessed one.
 */
export const toUnitEndAdornment = (
  label: string | null | undefined
): string | undefined => label?.replace(/^\$/, '') ?? undefined;

/**
 * Formats a price as `priceFormatter` does and appends its unit label without repeating the `$`:
 * `$10` + `$/MWh` -> `$10/MWh`. With no value or no label it returns the unitless price, so a
 * missing unit never falls back to `$/MWh`.
 *
 * This assumes every label from `/reference/price-units` is a US dollar unit starting with `$`
 * (today `$/MWh` and `$/kW-month`). A label without one is appended whole after the dollar
 * price, so a non-dollar unit would need its own handling here.
 *
 * @param value the price, or `null` for no price (rendered as `-`)
 * @param unitLabel the price unit's display label, such as `$/MWh` or `$/kW-month`
 * @param removeTrailingZeros passed through to `priceFormatter.format`
 */
export const formatPriceWithUnit = (
  value: number | null,
  unitLabel: string | undefined,
  removeTrailingZeros = true
): string => {
  const price = priceFormatter.format(value, removeTrailingZeros);
  if (value === null || !unitLabel) return price;
  return `${price}${unitLabel.replace(/^\$/, '')}`;
};

const _numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// For when we want to show numbers or prices without the currency symbol
export const numberFormatter = {
  format: (value: number | null) => {
    if (value === null) return '-';
    return _numberFormatter.format(value).replace('.00', '');
  },
};

/**
 * Formats a size as `numberFormatter` does, followed by a space and its unit label:
 * `1000` + `MWh/yr` -> `1,000 MWh/yr`. A null or undefined size is `-` with no unit, and with no
 * label it returns the unitless size, so a missing unit never falls back to `MWh/yr`.
 *
 * @param value the size, or `null`/`undefined` for no size (rendered as `-`)
 * @param unitLabel the size unit's display label from `/reference/volume-units`, such as `MWh/yr` or `MW`
 */
export const formatSizeWithUnit = (
  value: number | null | undefined,
  unitLabel: string | undefined
): string => {
  // `?? null` keeps an undefined value from being formatted as `NaN`.
  const size = numberFormatter.format(value ?? null);
  if (value == null || !unitLabel) return size;
  return `${size} ${unitLabel}`;
};

/**
 * Whether a position's `volumeUnit` says it is sized by Contracted Capacity (a BESS position)
 * rather than by an annual energy volume.
 *
 * @param unit the position's `volumeUnit`, or `null`/`undefined` when it is not known
 */
export const isSizedByCapacity = (unit?: VolumeUnit | null): boolean =>
  unit === VolumeUnit.MW;

/**
 * The quantity that sizes a bid: its Contracted Capacity when its `volumeUnit` is `MW`, otherwise
 * its requested volume. Reads `volumeUnit` rather than the product type, so the size always
 * matches the unit the backend resolved. A bid with no `volumeUnit` falls back to `requestedVolume`.
 *
 * @param bid the bid's unit and both size fields
 */
export const getBidSize = (
  bid: Pick<
    BidBasicDetails,
    'volumeUnit' | 'requestedVolume' | 'contractedCapacity'
  >
): number | null =>
  isSizedByCapacity(bid.volumeUnit)
    ? bid.contractedCapacity
    : bid.requestedVolume;

/**
 * Formats the quantity that sizes a bid, with its unit label: `getBidSize` formatted by
 * `formatSizeWithUnit`. Used by the bid views (Bid Management, Bid Details and the staging bid
 * card). A BESS bid's Contracted Capacity is stored to three decimals but shown to two, like
 * every size.
 *
 * @param bid the bid's unit and both size fields
 * @param unitLabel the size unit's display label, or `undefined` for a unitless size
 */
export const formatBidSize = (
  bid: Pick<
    BidBasicDetails,
    'volumeUnit' | 'requestedVolume' | 'contractedCapacity'
  >,
  unitLabel: string | undefined
): string => formatSizeWithUnit(getBidSize(bid), unitLabel);

export const getOfferStatus = (status: OFFER_STATUS) => {
  return OFFER_ACTIVE_STATUSES.includes(status)
    ? OFFER_STATUS.ACTIVE
    : OFFER_STATUS.DELISTED;
};
export const getBidStatus = (status: BID_STATUS) => {
  return BID_ACTIVE_STATUSES.includes(status)
    ? BID_STATUS.ACTIVE
    : BID_STATUS.DELISTED;
};

const isNegotiationStatus = (status: string) =>
  Object.keys(NEGOTIATION_STATUS_LABELS).includes(status);

/**
 * The status an offer's edit form starts from. Unlike getOfferStatus, which buckets every listed
 * status to ACTIVE for a new position, a negotiation status is kept: the form submits whatever it
 * holds, so seeding ACTIVE would tell the owner the offer is ACTIVE and ask the backend to make it
 * so. See RME-1250.
 */
export const getEditFormOfferStatus = (status: OFFER_STATUS) =>
  isNegotiationStatus(status) ? status : getOfferStatus(status);

/** The bid-side counterpart of getEditFormOfferStatus. */
export const getEditFormBidStatus = (status: BID_STATUS) =>
  isNegotiationStatus(status) ? status : getBidStatus(status);

export type StatusSelectOption = {
  id: string;
  name: string;
  disabled?: boolean;
};

/**
 * The options a position form's status select offers, given the status the form was populated with.
 *
 * A position in a negotiation status shows that status as its current value, but as a disabled
 * option: it is not the owner's to choose. ACTIVE is dropped alongside it, because to a negotiated
 * position ACTIVE means "stay listed", which it already is. DELISTED stays selectable.
 */
export const getStatusSelectOptions = (
  options: StatusSelectOption[],
  initialStatus: string | undefined
): StatusSelectOption[] => {
  if (!initialStatus || !isNegotiationStatus(initialStatus)) {
    return options;
  }
  return [
    {
      id: initialStatus,
      name: NEGOTIATION_STATUS_LABELS[initialStatus],
      disabled: true,
    },
    // ACTIVE is the same value on both status enums; the options serve bids and offers alike.
    ...options.filter(option => option.id !== OFFER_STATUS.ACTIVE),
  ];
};

export const getIsVppaOrPpa = (productType: PRODUCT_TYPE) => {
  return [PRODUCT_TYPE.VIRTUAL_PPA, PRODUCT_TYPE.PHYSICAL_PPA].includes(
    productType
  );
};

const BESS_PRODUCT_TYPES: ReadonlySet<PRODUCT_TYPE> = new Set([
  PRODUCT_TYPE.FULL_TOLL,
  PRODUCT_TYPE.TB_SWAP,
  PRODUCT_TYPE.FLOOR_UPSIDE_SHARE,
]);

/**
 * Whether a product type is a battery storage (BESS) product: Full Toll, TB Swap or Floor with
 * Upside Share. A blank or missing product type is not a BESS product.
 */
export const isBessProduct = (
  productType: PRODUCT_TYPE | '' | null | undefined
): boolean => !!productType && BESS_PRODUCT_TYPES.has(productType);

export const formatNumericString = (value: string | null): string | null => {
  if (!value) return value;
  const num = Number(value);
  return Number.isNaN(num) ? value : priceFormatter.format(num);
};

/**
 * Formats a credit support value for display with its unit label, without repeating the `$`:
 * `$50` + `$/kW` -> `$50/kW`. Used across credit support fields to maintain consistent formatting.
 * Accepts both number and string types to support execution terms (stored as strings).
 *
 * For numeric values (including comma-formatted strings like "1,000,000"), formats
 * with currency and the unit label: `$/MW` unless `unitLabel` says otherwise, and none
 * when it is empty. For non-numeric strings (prose like "As agreed"), returns the
 * original value as-is.
 *
 * @param unitLabel the unit's display label from `/reference/credit-support-units`
 */
export const formatCreditSupport = (
  value: number | string | null,
  unitLabel = '$/MW'
): string | null => {
  if (value === null || value === '') return null;
  const num =
    typeof value === 'string' ? Number(value.replaceAll(',', '')) : value;
  return Number.isNaN(num)
    ? String(value)
    : `${priceFormatter.format(num)}${unitLabel.replace(/^\$/, '')}`;
};

export const parseFloatStringToNumber = (value: string) => {
  return Number.parseFloat(value.replaceAll(',', ''));
};

/**
 * Parses a dollar amount and rounds it to cents, the way `NumberField` rounds it on blur. A form
 * whose column holds 2 decimal places sends this, so a value submitted before the field blurred
 * is rounded rather than rejected.
 */
export const parseRoundedToCents = (
  value: string | number | null | undefined
) => Number(parseFloatStringToNumber(String(value)).toFixed(2));
export const getProductTypeChipColor = (
  productTypeId: PRODUCT_TYPE
): 'productType.vppa' | 'productType.ppa' | 'productType.rec' => {
  if (productTypeId === PRODUCT_TYPE.VIRTUAL_PPA) return 'productType.vppa';
  if (productTypeId === PRODUCT_TYPE.PHYSICAL_PPA) return 'productType.ppa';
  return 'productType.rec';
};

/**
 * Derives the fee-paying party display name from a contract type string.
 * Mirrors the backend `FeePayingParty.from()` derivation rule:
 * NEW_BUILD_VPPA → "Seller"; all other types → "Buyer"; empty/null → "".
 *
 * Takes `string` rather than `CONTRACT_TYPE` because call sites pass the value
 * from React Hook Form's `watch('contractType')`, which is typed as `string` by
 * the form schema (`BidFormData` / offer `FormData`). Using `CONTRACT_TYPE` here
 * would require a cast at every call site.
 */
export const getFeePayingPartyName = (contractType: string): string => {
  if (!contractType) return '';
  return contractType === CONTRACT_TYPE.NEW_BUILD_VPPA ? 'Seller' : 'Buyer';
};

export const getContractTypeColor = (contractType: CONTRACT_TYPE) => {
  if (contractType === CONTRACT_TYPE.REC) {
    return 'bid.main';
  }
  if (
    CONTRACT_TYPE_BUILD_STAGE[contractType] === GROUPED_CONTRACT_TYPE.NEW_BUILD
  ) {
    return 'secondary';
  }
  return 'primary';
};

export const getIconTooltipLabel = (
  technologyId: TECHNOLOGY_TYPE,
  contractType: CONTRACT_TYPE
): string => {
  let techLabel: string;
  if (technologyId === TECHNOLOGY_TYPE.WIND_AND_SOLAR) {
    techLabel = 'Wind or Solar';
  } else if (technologyId === TECHNOLOGY_TYPE.WIND) {
    techLabel = 'Wind';
  } else if (technologyId === TECHNOLOGY_TYPE.BATTERY) {
    techLabel = 'Battery';
  } else {
    techLabel = 'Solar';
  }

  if (contractType === CONTRACT_TYPE.REC) return 'REC';

  const buildStage = CONTRACT_TYPE_BUILD_STAGE[contractType];
  if (buildStage === GROUPED_CONTRACT_TYPE.NEW_BUILD) {
    return `New Build ${techLabel}`;
  }
  if (buildStage === GROUPED_CONTRACT_TYPE.POST_BUILD) {
    return `Operational ${techLabel}`;
  }

  // Fallback for unrecognized or absent contract type
  return techLabel;
};

export const getTechnologyIcon = (
  technologyId: TECHNOLOGY_TYPE,
  contractType: CONTRACT_TYPE
) => {
  const color = getContractTypeColor(contractType);

  const iconProps = {} as SvgIconProps;

  if (contractType === CONTRACT_TYPE.REC) {
    iconProps['sx'] = { color };
  } else {
    iconProps['color'] = color as SvgIconOwnProps['color'];
  }

  const tooltipLabel = getIconTooltipLabel(technologyId, contractType);

  return (
    <Box sx={technologyIconWrapperSx}>
      {(technologyId === TECHNOLOGY_TYPE.WIND ||
        technologyId === TECHNOLOGY_TYPE.WIND_AND_SOLAR) && (
        <Tooltip title={tooltipLabel}>
          <Box sx={customIconWrapperSx}>
            <WindTechnologyIcon {...iconProps} />
          </Box>
        </Tooltip>
      )}
      {(technologyId === TECHNOLOGY_TYPE.SOLAR ||
        technologyId === TECHNOLOGY_TYPE.WIND_AND_SOLAR) && (
        <Tooltip title={tooltipLabel}>
          <Box sx={customIconWrapperSx}>
            <SolarTechnologyIcon {...iconProps} />
          </Box>
        </Tooltip>
      )}
      {technologyId === TECHNOLOGY_TYPE.BATTERY && (
        <Tooltip title={tooltipLabel}>
          <Box sx={customIconWrapperSx}>
            <BatteryChargingFull {...iconProps} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const getStatusIcon = (statusId: OFFER_STATUS, statusName?: string) => {
  const title = statusName || statusId;
  const iconSx: SxProps<Theme> = {
    color: theme => {
      if (statusId === OFFER_STATUS.ACTIVE) return theme.palette.primary.main;
      if (statusId === OFFER_STATUS.EXECUTED)
        return theme.palette.executed.main;
      return theme.palette.existingInterest.main;
    },
    '&.MuiSvgIcon-root': {
      width: '10px !important',
      height: '10px !important',
    },
  };

  const hasIcon =
    statusId === OFFER_STATUS.ACTIVE ||
    statusId === OFFER_STATUS.EXISTING_INTEREST ||
    statusId === OFFER_STATUS.EXECUTED;

  if (!hasIcon) {
    return title;
  }

  return (
    <Tooltip title={title}>
      <Lens fontSize="small" sx={iconSx} />
    </Tooltip>
  );
};

/**
 * Renders the required-match affordance for a field a counterparty must match —
 * a closed padlock with a tooltip naming the field.
 *
 * Marks the **required** state; a relaxed field is left unmarked. This is the
 * inverse of what the read surfaces first shipped: product asked for the hard
 * requirement to be the marked state, because that is the constraint a
 * counterparty reading the modal needs to notice.
 *
 * Callers decide when to render it, and the rule they follow is to read only
 * what can actually vary. Technology and Applicable REC Program key off the
 * value alone — `WIND_AND_SOLAR` and `ANY` already express flexibility, and
 * there is no "concrete value, flexible requirement" case to support — so their
 * flags are not read. The bid rule excludes `WIND_AND_SOLAR` while the offer
 * rule does not, because that wildcard is bid-side only: a wind-and-solar
 * project is a concrete hybrid a buyer must still match. The flags that are
 * read — Contract Type, Settlement Point and Settlement Point Index, on the bid
 * side — are read as `!== false`, because an absent flag resolves to `TRUE`
 * server-side. Product Type and ISO are marked unconditionally: product type is
 * a discriminator, and ISO has no flag at all. The per-field reasoning sits with
 * the callers, `BidDetails` and `OfferDetails`.
 *
 * The tooltip title and the `aria-label` are deliberately the same string:
 * hover meaning is unavailable on touch and invisible when tabbing a DataGrid
 * cell, so the accessible name has to carry it too. `aria-hidden` and `role`
 * are set explicitly because MUI's `SvgIcon` hides itself from assistive tech
 * unless `titleAccess` is used, which would place the text in a `<title>`
 * rather than the `aria-label` the read surfaces are tested against.
 *
 * Phrased position-centrically rather than with "you": both the owner and the
 * counterparty open the Details modals.
 *
 * @param fieldLabel The field's display label, e.g. `Contract Type`.
 */
export const getExactMatchIcon = (fieldLabel: string) => {
  const label = `Counterparties must match this ${fieldLabel}.`;

  return (
    <Tooltip title={label} slotProps={{ tooltip: { sx: exactMatchTooltipSx } }}>
      <Lock
        fontSize="small"
        sx={exactMatchIconSx}
        aria-label={label}
        role="img"
        aria-hidden={false}
      />
    </Tooltip>
  );
};

export const getCreditProfileAbbreviation = (
  creditProfile: COMPANY_CREDIT_PROFILE
) => {
  switch (creditProfile) {
    case COMPANY_CREDIT_PROFILE.LETTER_OF_CREDIT:
      return 'LC';
    case COMPANY_CREDIT_PROFILE.OTHER:
      return 'Other';
    case COMPANY_CREDIT_PROFILE.INVESTMENT_GRADE:
      return 'IG';
    case COMPANY_CREDIT_PROFILE.NON_INVESTMENT_GRADE:
      return 'Non-IG';
    case COMPANY_CREDIT_PROFILE.PARENTAL_GUARANTEE_FROM_INVESTMENT_GRADE_ENTITY:
      return 'PG from IG';
    case COMPANY_CREDIT_PROFILE.PARENTAL_GUARANTEE_FROM_NON_INVESTMENT_GRADE_ENTITY:
      return 'PG from Non-IG';
    case COMPANY_CREDIT_PROFILE.PROJECT_LIEN:
      return 'PL';
    default:
      return null;
  }
};

export const getTimezoneAbbr = (timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(new Date())
    .find(p => p.type === 'timeZoneName')?.value ?? timezone;

/**
 * Whether the company may see the product types that require SEF consent.
 *
 * A company that signed only the non-SEF (Physical) agreement may not. One that signed neither is
 * left unrestricted, as the backend's CompanyProductAccessService does. Which product types require
 * SEF consent comes from `requiresSefConsent` on /reference/product-types, not from a list here.
 */
export const companySeesSefProducts = (company: Company) =>
  company.isSefParticipantAgreementConsent ||
  !company.nonSefParticipantAgreementConsentDate;

/**
 * Reverse lookup from a SCREAMING_SNAKE_CASE `PositionTag` constant name, as the API returns it
 * in `MarketActivityEvent.positionTags` and `/reference/position-tags`, to its `POSITION_TAGS`
 * value. For example, `"BESS_OPTION"` maps to `POSITION_TAGS.BESS_OPTION` (`"bessOption"`).
 */
export const POSITION_TAGS_BY_ENUM_KEY: Record<string, POSITION_TAGS> =
  Object.fromEntries(
    Object.entries(POSITION_TAGS).map(([enumKey, enumValue]) => [
      enumKey,
      enumValue,
    ])
  );

export const getPositionTagsValues = (
  data: BidDTO | OfferDTO | { positionTags: string[] } | null | undefined
) => {
  if (!data) return [];

  // MarketActivityEvent (and any future type) provides positionTags as a
  // string array of SCREAMING_SNAKE_CASE enum keys (e.g. "BESS_OPTION");
  // BidDTO/OfferDTO use individual boolean fields keyed by camelCase value.
  if (Array.isArray((data as { positionTags: string[] }).positionTags)) {
    const tags = (data as { positionTags: string[] }).positionTags;
    return tags
      .map(key => POSITION_TAGS_BY_ENUM_KEY[key])
      .filter((tag): tag is POSITION_TAGS => tag !== undefined)
      .map(tag => POSITION_TAGS_OPTIONS[tag]);
  }

  const list = Object.values(POSITION_TAGS).filter(
    tagKey => !!(data as BidDTO | OfferDTO)[tagKey]
  );

  return list.map(tagKey => POSITION_TAGS_OPTIONS[tagKey]);
};
